import { Box, CircularProgress, Grid, Typography } from "@material-ui/core";
import { useQuery } from "@tanstack/react-query";
import GenericButton from "components/Buttons/GenericButton";
import GenericDialog from "components/Dialogs/GenericDialogV2";
import FormDropdown from "components/FormFields/FormDropdown";
import FormInputV2 from "components/FormFields/FormInputV2";
import FormMaskedInput from "components/FormFields/FormMaskedInput";
import FormDatePicker from "components/Forms/FormDatePicker";
import FormInput from "components/Forms/FormInput";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import PreviewDocument from "components/PreviewDocument";
import { getEmployeeDetails } from "crud/rent.crud";
import { getEmployeeDocumentById, getMyWorkCenters } from "crud/workCentersEmployer.crud";
import { ConditionGuard } from "guards/ConditionGuard";
import { useDataset } from "hooks/useDataset";
import useTokenHelper from "hooks/useTokenHelper";
import moment from "moment";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { errorMessages } from "resources/resources";
import { COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES_ID, DATASET, INPUT_MASKS, REGISTRATION_CONSTS } from "utils/const";
import { getKeyValueByCode } from "utils/datasets";
import { hasContractSalaryAlert, SALARY_ALERT_DISCLAIMER } from "utils/salaryAlert";
import { Alert, AlertTitle } from "@material-ui/lab";

export const EmployeeDialogDetails = ({ isOpen, closeModal, modalData }) => {
  const methods = useForm();

  const id = useMemo(() => modalData, [modalData]);
  const intl = useIntl();

  const { isFetching } = useQuery(["rent", "getEmployeeDetails", id], () => getEmployeeDetails(id), {
    enabled: !!id,
    onSuccess: (data) => {
      methods.reset({
        ...data,
        dateOfBirth: moment(data?.dateOfBirth),
        identityDocumentIssueDate: moment(data?.identityDocumentIssueDate),
        identityDocumentExpiryDate: moment(data?.identityDocumentExpiryDate),
        addmissionDate: moment(data?.addmissionDate),
      });
    },
  });

  const tokenHelper = useTokenHelper();

  const { data: WORK_CENTERS } = useQuery(["my-workcenters"], () => getMyWorkCenters(), {
    enabled: !!tokenHelper?.getUserId(),
    select: (data) => {
      if (!data) return [];

      return data.map((item) => ({
        code: item.id,
        label: item.value,
      }));
    },
  });

  const { data: PROVINCES } = useDataset({ name: DATASET.PROVINCES });

  const provinceId = methods.watch("provinceId");
  const provinceKeyValue = useMemo(() => getKeyValueByCode(PROVINCES, provinceId), [PROVINCES, provinceId]);

  const { data: MUNICIPALITIES } = useDataset(
    { name: DATASET.MUNICIPALITIES, parentKeyValue: provinceKeyValue },
    {
      enabled: !!provinceKeyValue,
    }
  );

  const { data: MARITAL_STATUS } = useDataset({ name: DATASET.MARITAL_STATUS });
  const { data: GENDER } = useDataset({ name: DATASET.GENDER });
  const { data: ACADEMIC_LEVEL } = useDataset({ name: DATASET.ACADEMIC_LEVEL });
  const { data: NATIONALITIES } = useDataset({ name: DATASET.NATIONALITIES });
  const { data: PROFESSIONAL_CATEGORY } = useDataset({ name: DATASET.PROFESSIONAL_CATEGORY });
  const { data: EMPLOYMENT_PROFESSIONAL_CATEGORY } = useDataset({ name: DATASET.EMPLOYMENT_PROFESSIONAL_CATEGORY });
  const { data: EMPLOYMENT_STATUS } = useDataset({ name: DATASET.EMPLOYMENT_STATUS });
  const { data: COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES } = useDataset({ name: DATASET.COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES });

  const documentTypeId = methods.watch("documentTypeId");

  const documentWatcher = methods.watch("documents");
  const documents = useMemo(
    () =>
      documentWatcher?.map((doc) => ({
        documentId: doc.id,
        ...doc,
      })) || [],
    [documentWatcher]
  );

  const isPassport = documentTypeId === COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES_ID.PASSPORT;

  const isBI = documentTypeId === COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES_ID.IDENTITY_CARD;

  const salaryStatedInTheContract = methods.watch("salaryStatedInTheContract");
  const HasSalaryMinimumAlert = methods.watch("HasSalaryMinimumAlert");
  const hasSalaryMinimumAlert = methods.watch("hasSalaryMinimumAlert");
  const showSalaryAlertDisclaimer = useMemo(
    () =>
      hasContractSalaryAlert({
        HasSalaryMinimumAlert,
        hasSalaryMinimumAlert,
        salaryStatedInTheContract,
      }),
    [HasSalaryMinimumAlert, hasSalaryMinimumAlert, salaryStatedInTheContract]
  );

  return (
    <FormProvider {...methods}>
      <form>
        <GenericDialog open={isOpen} onClose={closeModal} maxWidth="lg">
          <PageTitleHeader title={"Detalhes do Trabalhador"} />

          <Box>
            <Grid item xs={12} md={12}>
              <Typography variant="h3" style={{ marginBottom: "1rem" }}>
                Ao adicionar um novo registo de trabalhador, o sistema irá actualizar na área de utilizador a lista de
                trabalhadores registados da sua empresa.
              </Typography>
            </Grid>
          </Box>

          <ConditionGuard
            condition={!isFetching}
            fallback={
              <Box display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
              </Box>
            }
          >
            <Grid container>
              {showSalaryAlertDisclaimer && (
                <Grid item xs={12} md={12}>
                  <Alert
                    severity="error"
                    style={{
                      marginBottom: "1rem",
                    }}
                  >
                    <AlertTitle>Atenção: </AlertTitle>
                    {SALARY_ALERT_DISCLAIMER}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} md={12}>
                <Typography variant="h2" style={{ color: "#002e9e" }}>
                  Dados Pessoais
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInputV2
                  name="name"
                  label="Nome do Trabalhador"
                  placeholder="Insira o nome do trabalhador"
                  required
                  control={methods.control}
                  disabled
                />
              </Grid>{" "}
              <Grid item xs={12} md={3}>
                <FormDatePicker
                  name="dateOfBirth"
                  label="Data de Nascimento"
                  control={methods.control}
                  rules={{ required: false }}
                  getValues={methods.getValues}
                  setValue={methods.setValue}
                  trigger={methods.trigger}
                  maxDateToday={true}
                  minDateToday={false}
                  isRequired={true}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Género"
                  placeholder="Seleccione o Género"
                  control={methods.control}
                  name="genderId"
                  options={GENDER}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Estado civil"
                  placeholder="Seleccione o Estado Civil"
                  control={methods.control}
                  name="maritalStatusId"
                  options={MARITAL_STATUS}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Nível Académico"
                  placeholder="Seleccione o nível académico"
                  control={methods.control}
                  name="academicLevelId"
                  options={ACADEMIC_LEVEL}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInputV2
                  name="nif"
                  placeholder="Insira o NIF"
                  required
                  control={methods.control}
                  disabled
                  rules={{
                    required: {
                      value: true,
                      message: intl.formatMessage(errorMessages.ErrorMessage_FieldRequired),
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInputV2
                  name="ssn"
                  label="NISS"
                  placeholder="Insira o NISS"
                  required
                  control={methods.control}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormDropdown
                  label="Nacionalidade"
                  control={methods.control}
                  name="nationalityId"
                  options={NATIONALITIES}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormDropdown
                  control={methods.control}
                  label="Província"
                  name="provinceId"
                  options={PROVINCES}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormDropdown
                  control={methods.control}
                  label="Município"
                  name="municipalityId"
                  options={MUNICIPALITIES}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormInputV2
                  name="address"
                  label="Morada"
                  placeholder="Insira a morada do trabalhador"
                  required
                  control={methods.control}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Documento de Identificação"
                  placeholder="Seleccione o documento de identificação"
                  control={methods.control}
                  name="documentTypeId"
                  options={COLLECTIVE_EMPLOYEE_DOCUMENT_TYPES}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInputV2
                  name="identityDocumentNumber"
                  label="N.º do Documento de Identificação"
                  placeholder="Insira n.º de documento"
                  control={methods.control}
                  disabled
                  required
                  maxLength={isBI ? REGISTRATION_CONSTS.MAX_LENGTH_ID_CARD : null}
                  rules={{
                    validate: {
                      isBIValid: (value) => {
                        if (isBI && !/^[a-zA-Z0-9]{14}$/.test(value)) {
                          return "O BI deve conter no máximo 14 dígitos.";
                        }
                        return true;
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDatePicker
                  name="identityDocumentIssueDate"
                  label="Data de Emissão"
                  control={methods.control}
                  rules={{ required: false }}
                  getValues={methods.getValues}
                  setValue={methods.setValue}
                  trigger={methods.trigger}
                  maxDateToday={true}
                  minDateToday={false}
                  endDatename="endDate"
                  isRequired={true}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDatePicker
                  name="identityDocumentExpiryDate"
                  label="Data de Validade"
                  control={methods.control}
                  rules={{ required: false }}
                  getValues={methods.getValues}
                  setValue={methods.setValue}
                  trigger={methods.trigger}
                  maxDateToday={false}
                  minDateToday={true}
                  endDatename="endDate"
                  isRequired={true}
                  disabled
                />
              </Grid>
              <ConditionGuard condition={isPassport}>
                <Grid item xs={12} md={3}>
                  <FormInputV2
                    name="workVisaNumber"
                    label="Nº Visto de Trabalho"
                    placeholder="Insira nº do Visto de Trabalho"
                    control={methods.control}
                    disabled
                    required
                  />
                </Grid>
              </ConditionGuard>
              <Grid
                item
                xs={12}
                md={12}
                style={{
                  marginBottom: "1rem",
                }}
              >
                <PreviewDocument title="Documentos Anexados" documents={documents} promise={getEmployeeDocumentById} />
              </Grid>
              <Grid item xs={12} md={12}>
                <Typography variant="h2" style={{ color: "#002e9e" }}>
                  Dados Profissionais
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDatePicker
                  name="addmissionDate"
                  label="Data de Admissão"
                  control={methods.control}
                  rules={{ required: true }}
                  getValues={methods.getValues}
                  setValue={methods.setValue}
                  trigger={methods.trigger}
                  maxDateToday={true}
                  minDateToday={false}
                  isRequired={true}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Centro de Trabalho"
                  placeholder="Seleccione o centro de trabalho"
                  control={methods.control}
                  name="workCenterId"
                  options={WORK_CENTERS}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Situação Profissional"
                  placeholder="Seleccione a situação profissional"
                  control={methods.control}
                  name="employmentStatusId"
                  options={EMPLOYMENT_STATUS}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Categoria Profissional"
                  placeholder="Seleccione a categoria profissional"
                  control={methods.control}
                  name="professionalCategoryId"
                  options={EMPLOYMENT_PROFESSIONAL_CATEGORY}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormDropdown
                  label="Profissão"
                  placeholder="Seleccione a Profissão"
                  control={methods.control}
                  name="primaryFunctionId"
                  options={PROFESSIONAL_CATEGORY}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormMaskedInput
                  name="salaryStatedInTheContract"
                  label="Salário Expresso no Contrato"
                  placeholder="Insira o salário expresso no contrato"
                  required
                  control={methods.control}
                  mask={INPUT_MASKS.MONETARY}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormMaskedInput
                  name="baseSalary"
                  label="Remuneração Total"
                  placeholder="Insira a remuneração total"
                  required
                  control={methods.control}
                  mask={INPUT_MASKS.MONETARY}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Typography variant="h2" style={{ color: "#002e9e" }}>
                  Outros dados
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name="normalWorkingHours"
                  label="Horas Normais"
                  placeholder="Insira as horas normais"
                  control={methods.control}
                  errors={methods.formState.errors}
                  numberType
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name="weeklyWorkingHours"
                  label="Horas Semanais"
                  placeholder="Insira as horas semanais"
                  control={methods.control}
                  errors={methods.formState.errors}
                  numberType
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name="regularProvision"
                  label="Prestação Regular"
                  placeholder="Insira a prestação regular"
                  control={methods.control}
                  errors={methods.formState.errors}
                  numberType
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name="irregularProvision"
                  label="Prestação Irregular"
                  placeholder="Insira a prestação irregular"
                  control={methods.control}
                  errors={methods.formState.errors}
                  numberType
                  disabled
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={12}
                style={{
                  gap: "1rem",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <GenericButton typeSubmit={false} size="medium" color="tertiary" onClick={closeModal}>
                  Fechar
                </GenericButton>
              </Grid>
            </Grid>
          </ConditionGuard>
        </GenericDialog>
      </form>
    </FormProvider>
  );
};
